import { Check, Image, Plus, Type, X } from "lucide-react-native";
import { FC, useCallback, useMemo } from "react";

import {
  Button,
  ButtonText,
  Checkbox,
  CheckboxIcon,
  CheckboxIndicator,
  CheckboxLabel,
  DropdownPicker,
  HStack,
  Input,
  InputField,
  Pressable,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import { VariableDef } from "@/types/resources";

interface VariablesEditorProps {
  onChange?: (variables: VariableDef[]) => void;
  /** Use-case entities fix their schema in the template — render it locked. */
  readOnly?: boolean;
  variables: VariableDef[];
}

/**
 * The variable schema for a section/question: flat rows where each row names a
 * variable, sets its type (text / image) and whether it's required. The cells
 * below (`CellsEditor`) draw one input per variable. Free entities edit the
 * schema; use-case entities inherit it from the template and render read-only.
 */
const VariablesEditor: FC<VariablesEditorProps> = ({
  onChange,
  readOnly = false,
  variables,
}) => {
  const { i18n } = useLocalization();

  /* Names that appear on more than one variable — flagged inline so the user
     sees why saving is blocked (cells key their values by name). */
  const duplicateNames = useMemo(() => {
    const counts = new Map<string, number>();
    variables.forEach((variable) => {
      const name = variable.name.trim();
      if (name) counts.set(name, (counts.get(name) ?? 0) + 1);
    });
    return new Set(
      [...counts.entries()].filter(([, n]) => n > 1).map(([name]) => name)
    );
  }, [variables]);

  /* A name is invalid when it holds anything other than letters/digits (spaces,
     punctuation) — mirrors the backend charset check so saving is blocked. */
  const isInvalidName = useCallback(
    (name: string) => name.trim().length > 0 && !/^[A-Za-z0-9]+$/.test(name),
    []
  );

  const update = useCallback(
    (index: number, patch: Partial<VariableDef>) =>
      onChange?.(
        variables.map((v, i) => (i === index ? { ...v, ...patch } : v))
      ),
    [onChange, variables]
  );

  const remove = useCallback(
    (index: number) => onChange?.(variables.filter((_, i) => i !== index)),
    [onChange, variables]
  );

  const add = useCallback(
    () =>
      onChange?.([...variables, { is_required: true, name: "", type: "text" }]),
    [onChange, variables]
  );

  const typeItems = [
    { label: i18n.t("studies.cells.typeText"), value: "text" },
    { label: i18n.t("studies.cells.typeImage"), value: "image" },
  ];

  return (
    <VStack className="gap-3">
      <VStack className="gap-0.5">
        <Text className="text-sm font-semibold text-typography-800">
          {i18n.t("studies.cells.variables")}
        </Text>
        {/* Only free sections author their own questions with {{refs}};
            use-case templates already wire the placeholders in. */}
        {!readOnly && (
          <Text className="text-xs text-typography-500">
            {i18n.t("studies.cells.variablesHint")}{" "}
            <Text className="text-xs font-medium text-primary-600">
              {"{{myVariable}}"}
            </Text>
          </Text>
        )}
      </VStack>

      <VStack className="gap-2 pl-4">
        {variables.map((variable, index) => (
          <HStack key={index} className="items-center gap-2.5">
            {readOnly ? (
              <Text className="flex-1 text-sm text-typography-900">
                {variable.name}
              </Text>
            ) : (
              <Input
                className="flex-1 border border-outline-200 rounded-xl bg-background-50 data-[invalid=true]:web:ring-0 data-[invalid=true]:data-[focus=true]:web:ring-1"
                isInvalid={
                  duplicateNames.has(variable.name.trim()) ||
                  isInvalidName(variable.name)
                }
              >
                <InputField
                  onChangeText={(name) => update(index, { name })}
                  placeholder={i18n.t("studies.cells.fieldNamePlaceholder")}
                  value={variable.name}
                />
              </Input>
            )}

            <HStack className="items-center gap-1.5">
              {variable.type === "image" ? (
                <Image color="rgb(var(--color-typography-500))" size={16} />
              ) : (
                <Type color="rgb(var(--color-typography-500))" size={16} />
              )}
              {readOnly ? (
                <Text className="w-20 text-sm text-typography-600">
                  {variable.type === "image"
                    ? i18n.t("studies.cells.typeImage")
                    : i18n.t("studies.cells.typeText")}
                </Text>
              ) : (
                <DropdownPicker
                  className="w-28"
                  items={typeItems}
                  onChange={(value) =>
                    update(index, { type: value as VariableDef["type"] })
                  }
                  placeholder={i18n.t("studies.cells.type")}
                  size="sm"
                  value={variable.type}
                />
              )}
            </HStack>

            <Checkbox
              isChecked={variable.is_required}
              isDisabled={readOnly}
              onChange={(isChecked) =>
                update(index, { is_required: isChecked })
              }
              size="sm"
              value={`variable-${index}`}
            >
              <CheckboxIndicator className="data-[checked=true]:bg-primary-500 data-[checked=true]:border-primary-500">
                <CheckboxIcon as={Check} className="text-white" size="sm" />
              </CheckboxIndicator>
              <CheckboxLabel className="text-sm text-typography-700">
                {i18n.t("studies.cells.required")}
              </CheckboxLabel>
            </Checkbox>

            {!readOnly && (
              <Pressable
                accessibilityLabel={i18n.t("studies.cells.removeField")}
                className="p-1.5 rounded-md hover:bg-error-50"
                onPress={() => remove(index)}
              >
                <X color="rgb(var(--color-typography-500))" size={16} />
              </Pressable>
            )}
          </HStack>
        ))}

        {!readOnly && (
          <Button
            className="self-start rounded-lg border-dashed"
            onPress={add}
            size="sm"
            variant="outline"
          >
            <Plus color="rgb(var(--color-typography-600))" size={14} />
            <ButtonText className="text-sm text-typography-600 ml-1">
              {i18n.t("studies.cells.addField")}
            </ButtonText>
          </Button>
        )}
      </VStack>
    </VStack>
  );
};

export default VariablesEditor;
